import { GQLImageFragment, GQLKeystoneFileFragment } from '@theprintbar/graphql';
import { Position, getBlobUrl, invariant } from '..';

export enum ImageSize {
	Tiny = 50,
	Small = 100,
	Medium = 250,
	Large = 500,
	XLarge = 650,
	HD = 1080,
	ProductFull = 1500,
	Original = 0,
}

export function sizedImage(image: string | GQLImageFragment, size: ImageSize, png = false) {
	const url = typeof image === 'string' ? image : image.url;
	if (!url.startsWith('http')) return url;
	const imagePath = new URL(url);

	//Cap size
	const ensuredSize = Math.min(size, 12000);
	//Only files under 70MB get resized
	if (typeof image === 'string' || image.filesize < 70 * 1024 * 1024) {
		imagePath.searchParams.set('size', `${ensuredSize}`);
		if (png) imagePath.searchParams.set('format', 'png');
	}
	return imagePath.toString();
}

interface ImageSourceSetProps {
	url: string;
	originalWidth: number;
	originalHeight: number;
	png?: boolean;
	sizes?: ImageSize[];
}

//Sizes used for canvas background images. The background fills the whole canvas, so mobile
//(~400px CSS, up to 3x DPR) tops out around HD while desktop can still ask for the full render.
export const backgroundImageSizes = [ImageSize.Large, ImageSize.XLarge, ImageSize.HD, ImageSize.ProductFull];

/**
 * Builds a CE.SDK source set for an image, so the engine can pull a smaller render on small or
 * low-DPR canvases instead of always fetching the full-res source.
 *
 * Defaults to every ImageSize, capped by the source's own size. `originalWidth`/`originalHeight`
 * must therefore be the real pixel dimensions of `url` — pass them in pixels, not block design
 * units, or the ladder is cut to the wrong place. The untouched `url` is always appended last as
 * the full-res entry.
 */
export function getSizedImageSourceSet({ url, originalWidth, originalHeight, png = false, sizes }: ImageSourceSetProps) {
	const original = [
		{
			uri: url,
			width: originalWidth,
			height: originalHeight,
		},
	];

	//Anything at or above the original is pointless — the resizer won't upscale, and declaring a
	//size the source doesn't have makes the engine render it as if it were higher res than it is.
	const longestEdge = Math.max(originalWidth, originalHeight);
	//Object.values on a numeric enum yields the names too, hence the typeof guard.
	const allSizes = Object.values(ImageSize).filter(size => typeof size === 'number') as ImageSize[];
	const sourceSetSizes = [...new Set(sizes || allSizes)].filter(size => size > 0 && size < longestEdge).sort((a, b) => a - b);
	if (!sourceSetSizes.length || !originalWidth || !originalHeight) return original;

	const ratio = originalWidth / originalHeight;

	return [
		...sourceSetSizes.map(size => ({
			uri: sizedImage(url, size, png),
			width: ratio >= 1 ? size : Math.round(size * ratio),
			height: ratio >= 1 ? Math.round(size / ratio) : size,
		})),
		//Keep the untouched url last so consumers that identify the original by the absence of a
		//`size=` param (getGraphicData, computeSourceCropRect, soloRawRaster) still find it, and so
		//the engine has the full-res source to reach for on large/high-DPR canvases.
		...original,
	];
}

export function gqlImageToPrisma(fieldName: string, originalImage?: Omit<GQLImageFragment, 'url'> | null, overrideExtension?: string) {
	if (!originalImage) return {};

	const { id, extension, height, width, filesize } = originalImage;

	return {
		[`${fieldName}_id`]: id,
		[`${fieldName}_extension`]: overrideExtension || extension,
		[`${fieldName}_height`]: height,
		[`${fieldName}_width`]: width,
		[`${fieldName}_filesize`]: filesize,
	};
}

export function gqlFileToPrisma(fieldName: string, originalFile?: Pick<GQLKeystoneFileFragment, 'filename' | 'filesize'> | null) {
	if (!originalFile?.filename) return {};

	return {
		[`${fieldName}_filename`]: originalFile.filename,
		[`${fieldName}_filesize`]: originalFile.filesize,
	};
}

interface OriginalImage {
	image_id?: string | null;
	image_extension?: string | null;
	image_height?: number | null;
	image_width?: number | null;
	image_filesize?: number | null;
}
export function copyPrismaImage(fieldName: string, originalImage?: OriginalImage | null, overrideExtension?: string) {
	if (!originalImage) return {};

	return {
		[`${fieldName}_id`]: originalImage.image_id,
		[`${fieldName}_extension`]: overrideExtension || originalImage.image_extension,
		[`${fieldName}_height`]: originalImage.image_height,
		[`${fieldName}_width`]: originalImage.image_width,
		[`${fieldName}_filesize`]: originalImage.image_filesize,
	};
}

//get random square image
export const getRandomImage = () => {
	return `https://picsum.photos/500/500`;
};

export async function getBase64(file: File | Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

export async function cropImage(
	blob: Blob,
	position: Position,
	exclusions?: Array<{ left: number; top: number; width: number; height: number }>,
): Promise<Blob | null> {
	//Make sure we are on the client
	if (typeof document === 'undefined') return null;
	return new Promise((resolve, reject) => {
		const img = new Image();
		// Create a URL for the blob
		const url = URL.createObjectURL(blob);
		img.src = url;

		img.onload = () => {
			let sourceElement: HTMLImageElement | HTMLCanvasElement = img;
			// Create a canvas and draw the cropped part of the image onto it
			const canvas = document.createElement('canvas');
			const imgX = img.width * (position.left / 100);
			const imgY = img.height * (position.top / 100);
			const imgX2 = img.width * ((position.left + position.width) / 100);
			const imgY2 = img.height * ((position.top + position.height) / 100);

			// Add 1px padding
			const padding = 1;

			const roundedImgX = Math.max(0, Math.floor(imgX - padding));
			const roundedImgY = Math.max(0, Math.floor(imgY - padding));
			const roundedImgX2 = Math.min(img.width, Math.ceil(imgX2 + padding));
			const roundedImgY2 = Math.min(img.height, Math.ceil(imgY2 + padding));

			const roundedImgWidth = roundedImgX2 - roundedImgX;
			const roundedImgHeight = roundedImgY2 - roundedImgY;

			if (!!exclusions?.length) {
				const imageCanvas = document.createElement('canvas');
				imageCanvas.width = img.width;
				imageCanvas.height = img.height;
				const imageContext = imageCanvas.getContext('2d');
				if (!imageContext) {
					reject('Failed to get canvas context');
					return;
				}
				imageContext.drawImage(img, 0, 0);
				exclusions.forEach(exclusion => {
					imageContext.clearRect(exclusion.left, exclusion.top, exclusion.width, exclusion.height);
				});
				sourceElement = imageCanvas;
			}

			canvas.width = roundedImgWidth;
			canvas.height = roundedImgHeight;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.drawImage(
					sourceElement,
					roundedImgX,
					roundedImgY,
					roundedImgWidth,
					roundedImgHeight,
					0,
					0,
					roundedImgWidth,
					roundedImgHeight,
				);

				// Convert the canvas to a blob
				canvas.toBlob(croppedBlob => {
					if (croppedBlob) {
						resolve(croppedBlob);
					} else {
						reject(new Error('Failed to create blob from cropped image.'));
					}
				});
			} else {
				reject(new Error('Failed to get canvas context.'));
			}

			// Release the blob URL
			URL.revokeObjectURL(url);
		};

		img.onerror = () => {
			reject(new Error('Failed to load image from blob.'));
		};
	});
}

export function getImageData(blob: Blob, crop = false): Promise<ImageData> {
	//Make sure we are on the client
	if (typeof document === 'undefined') {
		return Promise.reject('Not on the client');
	}
	return new Promise((resolve, reject) => {
		const img = new Image();
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		if (!context) {
			reject('Canvas not supported');
			return;
		}

		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;
			context.drawImage(img, 0, 0);

			resolve(context.getImageData(0, 0, canvas.width, canvas.height));
		};

		img.onerror = error => {
			console.log('error');
			reject(error);
		};

		const url = getBlobUrl(blob);
		img.src = url;
	});
}

export function blobToBuffer(blob: Blob): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			if (reader.result instanceof ArrayBuffer) {
				resolve(reader.result);
			} else {
				reject('Failed to read blob');
			}
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(blob);
	});
}

export function imageDataToDataUrl(imageData: ImageData): string {
	if (typeof document === 'undefined') {
		throw new Error('Not on the client');
	}

	const canvas = document.createElement('canvas');
	canvas.width = imageData.width;
	canvas.height = imageData.height;
	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('Canvas not supported');
	}

	context.putImageData(imageData, 0, 0);
	return canvas.toDataURL();
}

export function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				resolve(reader.result);
			} else {
				reject('Failed to read blob');
			}
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export function blobToPixels(blob: Blob): Promise<Uint8ClampedArray> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const buffer = reader.result as ArrayBuffer;
			const view = new Uint8ClampedArray(buffer);
			resolve(view);
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(blob);
	});
}

export function base64ToBlob(base64: string, type: string): Promise<Blob> {
	return new Promise((resolve, reject) => {
		// Ensure the base64 string is properly formatted
		const base64Data = base64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');
		const url = `data:${type};base64,${base64Data}`;

		fetch(url)
			.then(res => res.blob())
			.then(blob => resolve(blob))
			.catch(error => reject(error));
	});
}

type CroppedImageResult = {
	blob: Blob;
	width: number;
	height: number;
	top: number;
	left: number;
	bottom: number;
	right: number;
};

export async function cropTransparentEdges(blob: Blob): Promise<CroppedImageResult> {
	const { top, left, bottom, right, width, height, hasOpacity, canvas } = await getImageTransparentBorderDetails(blob);
	if (!hasOpacity) {
		return { blob, width: 1, height: 1, top, left, bottom, right };
	}

	const croppedCanvas = document.createElement('canvas');
	croppedCanvas.width = width;
	croppedCanvas.height = height;
	const croppedCtx = croppedCanvas.getContext('2d');
	if (!croppedCtx) throw new Error('Cropped canvas 2D context not available');

	croppedCtx.drawImage(canvas, left, top, width, height, 0, 0, width, height);

	const croppedBlob = await new Promise<Blob>(resolve => croppedCanvas.toBlob(b => resolve(b!), 'image/png'));

	return { blob: croppedBlob, width, height, top, left, bottom, right };
}

export async function ensureUnder100MP(blob: Blob): Promise<{ blob: Blob; width: number; height: number }> {
	//Ensure image is under 100 megapixels
	const img = await createImageBitmap(blob);
	const maxPixels = 100_000_000;
	if (img.width * img.height <= maxPixels) {
		return {
			blob,
			width: img.width,
			height: img.height,
		};
	}
	const scaleFactor = Math.sqrt(maxPixels / (img.width * img.height));
	const canvas = document.createElement('canvas');
	canvas.width = Math.floor(img.width * scaleFactor);
	canvas.height = Math.floor(img.height * scaleFactor);
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas 2D context not available');
	ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
	const resizedBlob = await new Promise<Blob>(resolve => canvas.toBlob(b => resolve(b!), blob.type));
	return {
		blob: resizedBlob,
		width: canvas.width,
		height: canvas.height,
	};
}

export function getImageIdAndExtension(fileName: string, keystoneImageTypes?: boolean) {
	const parts = fileName.split('.');
	invariant(parts.length >= 2, 'Invalid file name');
	const extension = parts.pop()?.toLowerCase().replace('jpeg', 'jpg');
	const id = parts.join('.');
	invariant(!!id, 'Invalid image id');
	invariant(!!extension, 'Invalid image extension');
	invariant(!keystoneImageTypes || ['png', 'jpg', 'webp'].includes(extension), 'Invalid image type');
	return { id, extension };
}

export async function getImageTransparentBorderDetails(image: Blob) {
	const img = await createImageBitmap(image);
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas 2D context not available');

	canvas.width = img.width;
	canvas.height = img.height;
	ctx.drawImage(img, 0, 0);

	const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
	const { data, width, height } = imageData;

	let top = height,
		left = width,
		right = 0,
		bottom = 0;
	let hasOpacity = false;

	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const index = (y * width + x) * 4;
			const alpha = data[index + 3];
			if (alpha !== 0) {
				hasOpacity = true;
				if (x < left) left = x;
				if (x > right) right = x;
				if (y < top) top = y;
				if (y > bottom) bottom = y;
			}
		}
	}

	return {
		top: hasOpacity ? top : 0,
		left: hasOpacity ? left : 0,
		right: hasOpacity ? right : width - 1,
		bottom: hasOpacity ? bottom : height - 1,
		width: hasOpacity ? right - left + 1 : 0,
		height: hasOpacity ? bottom - top + 1 : 0,
		hasOpacity,
		canvas,
	};
}
